import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "../../api";
import { toast } from "sonner";

export const QUOTE_MERGE_FIELDS = ["customer_name", "company_name", "property_address", "service_area",
  "site_notes", "visit_count", "frequency", "season_start", "season_end", "season_year", "valid_until",
  "payment_terms_days", "extra_visit_rates", "business_name", "business_phone", "business_email", "etransfer_email",
  "option_A_per_visit", "option_A_season_total", "option_A_extra_visit_rate",
  "option_B_per_visit", "option_B_season_total", "option_B_extra_visit_rate"];

const EMPTY = { name: "", business_line: "lawn", quote_type: "seasonal", title: "Seasonal Service Quote", scope_intro: "",
  visit_inclusions: [], season_text: "", billing_text: "", default_terms: [], exclusions_text: "", closing_text: "",
  defaults: { visit_count: 26, frequency: "weekly", valid_for_days: 30, payment_terms_days: 30,
              billing_model: "season_lump_sum_on_acceptance", price: null } };

function InsertField({ onPick, testid }) {
  return (
    <select className="field-input mt-1 w-full text-xs" value="" data-testid={testid}
      onChange={(e) => { if (e.target.value) onPick(`{${e.target.value}}`); e.target.value = ""; }}>
      <option value="">Insert field…</option>
      {QUOTE_MERGE_FIELDS.map((f) => <option key={f} value={f}>{`{${f}}`}</option>)}
    </select>
  );
}

export function QuoteTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [termsLib, setTermsLib] = useState({ standard: [], custom: [] });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = () => {
    api.get("/quote-templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/quote-terms").then((r) => setTermsLib(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setForm(t ? { ...EMPTY, ...t, defaults: { ...EMPTY.defaults, ...(t.defaults || {}) },
                  default_terms: t.default_terms || [] } : EMPTY);
    setEditing(t ? t.id : "new");
  };
  const save = async () => {
    try {
      const body = { ...form, visit_inclusions: (form.visit_inclusions || []).filter((x) => (x || "").trim()) };
      if (editing === "new") await api.post("/quote-templates", body);
      else await api.patch(`/quote-templates/${editing}`, body);
      toast.success("Template saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const dup = async (id) => { await api.post(`/quote-templates/${id}/duplicate`).catch((e) => toast.error(fmtErr(e))); load(); };
  const setDef = async (id) => { await api.post(`/quote-templates/${id}/set-default`).catch((e) => toast.error(fmtErr(e))); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete this template? Quotes already sent keep their snapshot.")) return;
    await api.delete(`/quote-templates/${id}`).catch((e) => toast.error(fmtErr(e)));
    load();
  };
  const setD = (k, v) => setForm((f) => ({ ...f, defaults: { ...f.defaults, [k]: v } }));
  const setInc = (i, v) => setForm((f) => { const l = [...f.visit_inclusions]; l[i] = v; return { ...f, visit_inclusions: l }; });
  const moveInc = (i, dir) => setForm((f) => {
    const l = [...f.visit_inclusions]; const j = i + dir;
    if (j < 0 || j >= l.length) return f;
    [l[i], l[j]] = [l[j], l[i]];
    return { ...f, visit_inclusions: l };
  });
  const allTerms = [...(termsLib.standard || []), ...(termsLib.custom || [])];

  return (
    <section className="card-flat space-y-3 p-5" data-testid="quote-templates-section">
      <div className="flex items-center justify-between">
        <p className="label-caps">Quote templates</p>
        <button data-testid="new-template-btn" onClick={() => openEdit(null)} className="text-sm font-bold text-primary">New template</button>
      </div>
      <p className="text-xs text-muted-foreground">A template fills in the scope of work, what's included each visit,
        the season and billing wording, exclusions and default terms.</p>
      {templates.map((t) => (
        <div key={t.id} className="flex items-center gap-2 rounded-md border border-border p-3" data-testid={`template-row-${t.id}`}>
          <div className="flex-1">
            <p className="text-sm font-semibold">{t.name}{t.is_default ? ` · default` : ""}</p>
            <p className="text-xs text-muted-foreground">{t.quote_type === "one_time" ? "one-time" : "seasonal"} · {t.business_line} · {(t.visit_inclusions || []).length} bullets · {(t.default_terms || []).length} default terms</p>
          </div>
          <button data-testid={`template-edit-${t.id}`} onClick={() => openEdit(t)} className="text-xs font-bold text-primary">Edit</button>
          <button data-testid={`template-dup-${t.id}`} onClick={() => dup(t.id)} className="text-xs font-bold text-primary">Duplicate</button>
          {!t.is_default && <button data-testid={`template-default-${t.id}`} onClick={() => setDef(t.id)} className="text-xs font-bold text-primary">Set default</button>}
          <button data-testid={`template-del-${t.id}`} onClick={() => del(t.id)} className="text-xs font-bold text-red-600">Delete</button>
        </div>
      ))}
      {editing && (
        <div className="space-y-2 rounded-md border-2 border-primary p-3" data-testid="template-editor">
          <label className="block text-xs font-bold">Name
            <input className="field-input mt-1 w-full" value={form.name} data-testid="tpl-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <div className="flex gap-2">
            <label className="text-xs font-bold">Quote type
              <select className="field-input mt-1" value={form.quote_type || "seasonal"} data-testid="tpl-qtype"
                onChange={(e) => setForm({ ...form, quote_type: e.target.value })}>
                <option value="seasonal">seasonal</option><option value="one_time">one-time</option>
              </select></label>
            <label className="text-xs font-bold">Business line
              <select className="field-input mt-1" value={form.business_line} data-testid="tpl-line"
                onChange={(e) => setForm({ ...form, business_line: e.target.value })}>
                <option value="lawn">lawn</option><option value="snow">snow</option><option value="other">other</option>
              </select></label>
            <label className="flex-1 text-xs font-bold">Title on the quote
              <input className="field-input mt-1 w-full" value={form.title} data-testid="tpl-title"
                onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          </div>
          <label className="block text-xs font-bold">Scope intro
            <textarea className="field-input mt-1 w-full" rows={2} value={form.scope_intro} data-testid="tpl-scope"
              onChange={(e) => setForm({ ...form, scope_intro: e.target.value })} /></label>
          <InsertField testid="tpl-scope-insert" onPick={(tok) => setForm((f) => ({ ...f, scope_intro: `${f.scope_intro}${tok}` }))} />
          <div>
            <p className="text-xs font-bold">Visit inclusions</p>
            {(form.visit_inclusions || []).map((inc, i) => (
              <div key={i} className="mb-1 flex items-center gap-1">
                <input className="field-input flex-1" value={inc} data-testid={`tpl-inc-${i}`} onChange={(e) => setInc(i, e.target.value)} />
                <button data-testid={`tpl-inc-${i}-up`} onClick={() => moveInc(i, -1)} className="px-1 text-xs font-bold">↑</button>
                <button data-testid={`tpl-inc-${i}-down`} onClick={() => moveInc(i, 1)} className="px-1 text-xs font-bold">↓</button>
                <button data-testid={`tpl-inc-${i}-del`} className="px-1 text-xs font-bold text-red-600"
                  onClick={() => setForm((f) => ({ ...f, visit_inclusions: f.visit_inclusions.filter((_, j) => j !== i) }))}>✕</button>
              </div>
            ))}
            <button data-testid="tpl-inc-add" className="text-xs font-bold text-primary"
              onClick={() => setForm((f) => ({ ...f, visit_inclusions: [...(f.visit_inclusions || []), ""] }))}>+ Add inclusion</button>
          </div>
          {[["season_text", "Season text", "tpl-season"], ["billing_text", "Billing text", "tpl-billing"],
            ["exclusions_text", "Exclusions", "tpl-exclusions"], ["closing_text", "Closing line", "tpl-closing"]].map(([k, lbl, tid]) => (
            <div key={k}>
              <label className="block text-xs font-bold">{lbl}
                <textarea className="field-input mt-1 w-full" rows={2} value={form[k]} data-testid={tid}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
              <InsertField testid={`${tid}-insert`} onPick={(tok) => setForm((f) => ({ ...f, [k]: `${f[k]}${tok}` }))} />
            </div>
          ))}
          {form.quote_type === "one_time" && (
            <label className="block text-xs font-bold">Default price ($, optional)
              <input type="number" className="field-input mt-1 w-full" value={form.defaults.price ?? ""} data-testid="tpl-def-price"
                onChange={(e) => setD("price", e.target.value === "" ? null : Number(e.target.value))} /></label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-bold">Default visits
              <input type="number" className="field-input mt-1 w-full" value={form.defaults.visit_count} data-testid="tpl-def-visits"
                onChange={(e) => setD("visit_count", Number(e.target.value))} /></label>
            <label className="text-xs font-bold">Frequency
              <select className="field-input mt-1 w-full" value={form.defaults.frequency} data-testid="tpl-def-freq"
                onChange={(e) => setD("frequency", e.target.value)}>
                <option value="weekly">weekly</option><option value="bi-weekly">bi-weekly</option>
              </select></label>
            <label className="text-xs font-bold">Valid days
              <input type="number" className="field-input mt-1 w-full" value={form.defaults.valid_for_days} data-testid="tpl-def-valid"
                onChange={(e) => setD("valid_for_days", Number(e.target.value))} /></label>
            <label className="text-xs font-bold">Payment terms (days)
              <input type="number" className="field-input mt-1 w-full" value={form.defaults.payment_terms_days} data-testid="tpl-def-terms"
                onChange={(e) => setD("payment_terms_days", Number(e.target.value))} /></label>
            <label className="col-span-2 text-xs font-bold">Billing model
              <select className="field-input mt-1 w-full" value={form.defaults.billing_model} data-testid="tpl-def-billing"
                onChange={(e) => setD("billing_model", e.target.value)}>
                <option value="season_lump_sum_on_acceptance">One invoice on acceptance</option>
                <option value="per_visit">Per visit</option>
                <option value="monthly">Monthly</option>
                <option value="seasonal">Seasonal instalments</option>
              </select></label>
          </div>
          <div>
            <p className="text-xs font-bold">Default terms (ticked on new quotes)</p>
            {allTerms.filter((t) => ["both", form.quote_type || "seasonal"].includes(t.applies_to || "both")).map((t) => (
              <label key={t.id} className="flex items-start gap-2 text-xs">
                <input type="checkbox" data-testid={`tpl-term-${t.id}`} checked={(form.default_terms || []).includes(t.id)}
                  onChange={(e) => setForm((f) => ({ ...f,
                    default_terms: e.target.checked ? [...(f.default_terms || []), t.id]
                                                    : (f.default_terms || []).filter((x) => x !== t.id) }))} />
                <span>{t.text.slice(0, 80)}{t.text.length > 80 ? "…" : ""}</span>
              </label>
            ))}
            <Link to="/settings/quotes#set-quote-terms" data-testid="tpl-manage-terms" className="text-xs font-bold text-primary">Manage terms library (below)</Link>
          </div>
          <div className="flex gap-2">
            <button data-testid="tpl-save" onClick={save}
              className="h-10 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">Save template</button>
            <button data-testid="tpl-cancel" onClick={() => setEditing(null)}
              className="h-10 flex-1 rounded-md border-2 border-border text-sm font-bold">Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
